import { Plugin, PluginKey } from '@tiptap/pm/state';
import { ReplaceStep } from '@tiptap/pm/transform';

/**
 * This plugin adds a space after the inline board link when it is created via paste.
 * This helps the UX in mobile, because if a space is not added, the auto-suggest features of the
 * keyboard can break the inline board link.
 *
 * I.e. Pressing a suggestion immediately after the inline board link will replace the inline board link
 *  with the suggestion, as well as mess up the text around it.
 *
 * This strategy won't prevent the user from doing so (e.g. if they delete the space, or they move the
 * cursor to just after the inline board link), but it will reduce the occurrences.
 */
export const createAddSpaceAfterInlineNodePlugin = (inlineNodeName: string) => {
    return new Plugin({
        key: new PluginKey(`inlineBoardLink-${inlineNodeName}`),
        appendTransaction: (transactions, oldState, newState) => {
            if (transactions.length < 2) return;

            // First transaction will be a paste
            if (!transactions[0].getMeta('paste')) return;

            const inlineBoardLinkTransaction = transactions[1];

            // The second transaction will have two steps in an inline board link creation operation,
            //  both of which are ReplaceSteps. The first one will replace the link text with nothing.
            //  The second one will add the inline board link node at that position.
            if (inlineBoardLinkTransaction.steps.length < 2) return;

            const inlineBoardLinkStep = inlineBoardLinkTransaction.steps[1];

            if (!(inlineBoardLinkStep instanceof ReplaceStep)) return;

            // The inline board link creation is at a particular position
            if (inlineBoardLinkStep.from !== inlineBoardLinkStep.to) return;

            const isInlineBoardLinkReplaceStep = inlineBoardLinkStep.slice.content.content.some(
                (node) => node.type.name === inlineNodeName,
            );

            if (!isInlineBoardLinkReplaceStep) return;

            const { tr } = newState;
            const { selection } = tr;

            tr.insertText(' ', selection.$head.pos);

            return tr;
        },
    });
};
